import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMe } from '../../hooks/useMe'
import {
  requestEmailChange,
  resendEmailChangeVerification,
  verifyEmailChange,
  requestPhoneOtp,
  verifyPhoneOtp,
  enableAdultContent,
  disableAdultContent,
} from '../../api/security'
import { getToken } from '../../lib/auth'
import { SettingsButton, SettingsCard, SettingsTitle } from '../../components/settings/SettingsUI'

type ConfirmMode = null | 'export' | 'close'

export default function AccountSettings() {
  const navigate = useNavigate()
  const authed = Boolean(getToken())
  const { me, refresh } = useMe(authed)
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(null)
  const [confirmText, setConfirmText] = useState('')

  const [emailOpen, setEmailOpen] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [emailCode, setEmailCode] = useState('')
  const [emailVerificationId, setEmailVerificationId] = useState<string | null>(null)
  const [emailBusy, setEmailBusy] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)

  const [phoneOpen, setPhoneOpen] = useState(false)
  const [phoneInput, setPhoneInput] = useState('')
  const [phoneCode, setPhoneCode] = useState('')
  const [phoneOtpRequested, setPhoneOtpRequested] = useState(false)
  const [phoneBusy, setPhoneBusy] = useState(false)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [adultBusy, setAdultBusy] = useState(false)
  const [adultError, setAdultError] = useState<string | null>(null)

  const displayName = me?.display_name || me?.profile?.display_name || me?.username || 'Your account'
  const handle = me?.primary_handle || me?.profile?.handle || me?.username || ''
  const avatarUrl = me?.avatar_url || me?.profile?.avatar_url || ''
  const email = me?.email || 'No email on file'
  const phone = getOptionalString(me?.phone)
  const memberSince = formatDate(me?.member_since || (me?.profile as any)?.created_at)
  const statusLabel = formatAccountStatus(me?.account_status || accountStatusFromProfile(me?.profile?.profile_visibility_status))

  const emailVerified = Boolean(me?.email_verified)
  const phoneVerified = Boolean(me?.phone_verified)
  const preferredContact = me?.preferred_contact || (phoneVerified ? 'phone' : emailVerified ? 'email' : 'none')
  const emailPending = Boolean((me as any)?.email_pending_verification)
  const pendingEmail = getOptionalString((me as any)?.pending_email)

  const requiresEmailVerification = Boolean(me?.seller_eligibility && !emailVerified)

  const isCreatorEnabled = Boolean(me?.creator_tools_enabled ?? me?.seller_eligibility?.can_publish)
  const hasProProfile = Boolean(me?.pro_profile_enabled)
  const managedPages = Number.isFinite(Number(me?.pages_managed_count)) ? Number(me?.pages_managed_count) : 0

  const ageGroup = (me?.age_group || 'unknown') as 'unknown' | 'child' | 'teen' | 'adult'
  const ageVerificationStatus = (me?.age_verification_status || 'not_set') as 'not_set' | 'self_attested' | 'verified'
  const sensitivePref = (me?.sensitive_content_pref || 'hide') as 'hide' | 'blur' | 'show'
  const dobSet = Boolean(me?.dob_set)
  const hasCardOnFile = Boolean(me?.has_payment_method_on_file)
  const adultTermsVersion = String(me?.adult_terms_version_current || 'v1')
  const adultAccessState = (me?.adult_access_state || 'locked') as 'locked' | 'eligible' | 'enabled'
  const showAgeSafetyBanner = ageGroup === 'unknown'

  const notificationsSummary = me?.notifications_summary || 'Enabled'
  const privacySummary = me?.privacy_summary || 'Public profile'
  const accessibilitySummary = me?.accessibility_summary || 'Coming soon'

  const emailBadge = emailPending ? 'Pending verification' : emailVerified ? 'Verified' : 'Unverified'
  const emailBadgeTone: 'ok' | 'warn' | 'muted' = emailPending ? 'warn' : emailVerified ? 'ok' : 'warn'
  const emailValue = emailPending && pendingEmail ? `${email} -> ${pendingEmail}` : email

  const phoneBadge = phone ? (phoneVerified ? 'Verified' : 'Unverified') : 'Not set'
  const phoneBadgeTone: 'ok' | 'warn' | 'muted' = phone ? (phoneVerified ? 'ok' : 'warn') : 'muted'

  const closeEmailModal = () => {
    setEmailOpen(false)
    setEmailInput('')
    setEmailCode('')
    setEmailVerificationId(null)
    setEmailBusy(false)
    setEmailError(null)
  }

  const closePhoneModal = () => {
    setPhoneOpen(false)
    setPhoneInput('')
    setPhoneCode('')
    setPhoneOtpRequested(false)
    setPhoneBusy(false)
    setPhoneError(null)
  }

  return (
    <div className="space-y-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
      <SettingsTitle title="Account" subtitle="Manage your identity and connections" />

      {requiresEmailVerification ? (
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          Verify your email to enable payouts and account recovery.
        </div>
      ) : null}
      {showAgeSafetyBanner ? (
        <div className="rounded-xl border border-sky-400/40 bg-sky-500/10 px-4 py-3 text-sm text-sky-100">
          Set your age to personalize content safety.
        </div>
      ) : null}

      <SettingsCard title="Identity" subtitle="Public profile basics and account status">
        <div className="flex items-start gap-3">
          <Avatar url={avatarUrl} alt={displayName} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-semibold text-white">{displayName}</div>
            <div className="truncate text-sm text-white/70">@{handle}</div>
            <div className="mt-1 text-xs text-white/60">Member since {memberSince}</div>
            <div className="mt-2 inline-flex rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-100">
              {statusLabel}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <LinkButton to="/settings/profile">Edit profile</LinkButton>
          <LinkButton to="/people/edit/new">Edit Profile page</LinkButton>
          <LinkRow to="/settings/handles" label="Primary handle" value={`@${handle}`} compact />
          <div className="min-h-11 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
            <div className="text-xs text-white/60">Username</div>
            <div className="truncate text-sm text-white/85">{me?.username || handle}</div>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Contact" subtitle="Verification and recovery contact points">
        <div className="space-y-2">
          <ActionRow
            label="Email"
            value={emailValue}
            actionLabel={emailPending ? 'Verify email' : 'Manage email'}
            onClick={() => {
              setEmailOpen(true)
              setEmailInput(pendingEmail || email)
              setEmailError(null)
            }}
            badge={emailBadge}
            badgeTone={emailBadgeTone}
          />
          <ActionRow
            label="Phone"
            value={phone || 'No phone added'}
            actionLabel={phone ? 'Change phone' : 'Add phone'}
            onClick={() => {
              setPhoneOpen(true)
              setPhoneInput(phone || '')
              setPhoneError(null)
            }}
            badge={phoneBadge}
            badgeTone={phoneBadgeTone}
          />
          <StaticRow label="Preferred contact" value={formatPreferredContact(preferredContact)} badgeTone="muted" />
        </div>
      </SettingsCard>

      <SettingsCard title="Quick Links" subtitle="Open detailed pages for wallet, security, and billing">
        <div className="space-y-2">
          <LinkRow to="/settings/wallet" label="Wallet" value="View PIXE, payouts, and transactions" />
          <LinkRow to="/settings/security" label="Security" value="Password, 2FA, and active sessions" />
          <LinkRow to="/settings/billing" label="Billing" value="Subscriptions and invoices" />
        </div>
      </SettingsCard>

      <SettingsCard title="Roles & Access" subtitle="Capabilities currently enabled on this account">
        <div className="space-y-2">
          <LinkRow to="/settings/pixe-publish" label="Creator tools" value={isCreatorEnabled ? 'Enabled' : 'Not enabled'} />
          <LinkRow to="/pros" label="Pro profile" value={hasProProfile ? 'Enabled' : 'Not enabled'} />
          <LinkRow to="/settings/pages" label="Manage Pixe Pages" value={managedPages > 0 ? `${managedPages} page${managedPages === 1 ? '' : 's'}` : 'No pages managed yet'} />
          {me?.is_root ? (
            <LinkRow to="/settings/moderation" label="Admin access" value="Root tools enabled" />
          ) : null}
        </div>
      </SettingsCard>

      <SettingsCard title="Preferences" subtitle="At-a-glance settings with quick navigation">
        <div className="space-y-2">
          <LinkRow to="/settings/notifications" label="Notifications" value={notificationsSummary} />
          <LinkRow to="/settings/privacy" label="Privacy" value={privacySummary} />
          <StaticRow label="Accessibility" value={accessibilitySummary} badge="Soon" badgeTone="muted" />
        </div>
      </SettingsCard>

      <SettingsCard title="Age & Content" subtitle="Age-safe defaults and sensitive content controls">
        {!dobSet ? (
          <div className="space-y-3">
            <LinkRow to="/settings/age" label="Set your birthday" value="Required for age-appropriate content controls" />
            <p className="text-xs text-white/60">Used to provide age-appropriate content and safety controls.</p>
            <LinkButton to="/settings/age">Set age</LinkButton>
          </div>
        ) : (
          <div className="space-y-2">
            <StaticRow label="Age group" value={formatAgeGroup(ageGroup)} badge="Age set" badgeTone="ok" />
            <StaticRow
              label="Adult access"
              value={formatAdultAccessState(adultAccessState)}
              badge={formatAdultAccessState(adultAccessState)}
              badgeTone={adultAccessState === 'enabled' ? 'ok' : 'warn'}
            />
            <StaticRow
              label="Age status"
              value={formatAgeVerificationStatus(ageVerificationStatus)}
              badge={formatAgeVerificationStatus(ageVerificationStatus)}
              badgeTone={ageVerificationStatus === 'verified' ? 'ok' : 'warn'}
            />
            <StaticRow
              label="Card on file"
              value={hasCardOnFile ? 'Added' : 'Not added'}
              badge={hasCardOnFile ? 'Ready' : 'Required'}
              badgeTone={hasCardOnFile ? 'ok' : 'warn'}
            />
            <LinkRow to="/settings/privacy" label="Sensitive content" value={formatSensitivePref(sensitivePref)} />
            <LinkButton to="/settings/age">Update birthday</LinkButton>
            {adultAccessState !== 'enabled' ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <LinkButton to="/settings/billing">Add card on file</LinkButton>
                <button
                  type="button"
                  disabled={adultBusy || !hasCardOnFile || ageGroup !== 'adult'}
                  onClick={async () => {
                    setAdultBusy(true)
                    setAdultError(null)
                    try {
                      await enableAdultContent({ accept_terms_version: adultTermsVersion })
                      await refresh()
                    } catch (e: any) {
                      setAdultError(e?.response?.data?.detail || e?.message || 'Could not enable adult content.')
                    } finally {
                      setAdultBusy(false)
                    }
                  }}
                  className="min-h-11 rounded-lg border border-white/15 bg-white/[0.03] px-4 py-2 font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                >
                  {adultBusy ? 'Enabling...' : 'Accept terms and enable'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={adultBusy}
                onClick={async () => {
                  setAdultBusy(true)
                  setAdultError(null)
                  try {
                    await disableAdultContent()
                    await refresh()
                  } catch (e: any) {
                    setAdultError(e?.response?.data?.detail || e?.message || 'Could not disable adult content.')
                  } finally {
                    setAdultBusy(false)
                  }
                }}
                className="min-h-11 rounded-lg border border-white/15 bg-white/[0.03] px-4 py-2 font-semibold text-white hover:bg-white/10 disabled:opacity-60"
              >
                {adultBusy ? 'Disabling...' : 'Disable adult content'}
              </button>
            )}
            {adultError ? <div className="text-sm text-amber-200">{adultError}</div> : null}
          </div>
        )}
      </SettingsCard>

      <SettingsCard title="Data & Danger Zone" subtitle="Sensitive actions require confirmation">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setConfirmMode('export')}
            className="flex min-h-11 w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-left hover:bg-white/10"
          >
            <span className="text-sm font-semibold text-white">Download my data</span>
            <span className="text-white/60">{'>'}</span>
          </button>
          <button
            type="button"
            onClick={() => setConfirmMode('close')}
            className="flex min-h-11 w-full items-center justify-between rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-left hover:bg-red-500/15"
          >
            <span className="text-sm font-semibold text-red-100">Close account</span>
            <span className="text-red-200/70">{'>'}</span>
          </button>
        </div>
      </SettingsCard>

      {confirmMode ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d1118] p-4">
            <div className="text-lg font-semibold text-white">{confirmMode === 'export' ? 'Request account export' : 'Close account'}</div>
            <p className="mt-2 text-sm text-white/70">
              {confirmMode === 'export'
                ? 'Type EXPORT to continue. You will be routed to support to complete this request.'
                : 'Type DELETE to continue. This opens account closure guidance and support.'}
            </p>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmMode === 'export' ? 'EXPORT' : 'DELETE'}
              className="mt-3 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 outline-none"
            />
            <div className="mt-4 flex justify-end gap-2">
              <SettingsButton
                variant="ghost"
                onClick={() => {
                  setConfirmMode(null)
                  setConfirmText('')
                }}
              >
                Cancel
              </SettingsButton>
              <SettingsButton
                variant={confirmMode === 'close' ? 'danger' : 'primary'}
                disabled={confirmText !== (confirmMode === 'export' ? 'EXPORT' : 'DELETE')}
                onClick={() => {
                  setConfirmMode(null)
                  setConfirmText('')
                  if (confirmMode === 'export') {
                    navigate('/settings/feedback?topic=data-export')
                    return
                  }
                  navigate('/settings/feedback?topic=close-account')
                }}
              >
                Continue
              </SettingsButton>
            </div>
          </div>
        </div>
      ) : null}

      {emailOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d1118] p-4">
            <div className="text-lg font-semibold text-white">Manage email</div>
            <p className="mt-2 text-sm text-white/70">
              Updating email requires verification. Until verified, your current email stays active.
            </p>
            <div className="mt-3">
              <div className="mb-1 text-sm text-white/70">New email</div>
              <input
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="name@example.com"
                className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 outline-none"
              />
            </div>
            <div className="mt-3">
              <div className="mb-1 text-sm text-white/70">Verification code</div>
              <input
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value)}
                placeholder="6-digit code"
                className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 outline-none"
              />
            </div>
            {emailError ? <div className="mt-3 text-sm text-amber-200">{emailError}</div> : null}
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <SettingsButton variant="ghost" onClick={closeEmailModal}>
                Close
              </SettingsButton>
              <SettingsButton
                variant="ghost"
                disabled={emailBusy || !emailPending}
                onClick={async () => {
                  setEmailBusy(true)
                  setEmailError(null)
                  try {
                    const res = await resendEmailChangeVerification()
                    setEmailVerificationId(res.verification_id || null)
                  } catch (e: any) {
                    setEmailError(e?.response?.data?.detail || e?.message || 'Could not resend code.')
                  } finally {
                    setEmailBusy(false)
                  }
                }}
              >
                Resend code
              </SettingsButton>
              <SettingsButton
                variant="primary"
                disabled={emailBusy || !emailInput.trim()}
                onClick={async () => {
                  setEmailBusy(true)
                  setEmailError(null)
                  try {
                    const res = await requestEmailChange({ email: emailInput.trim() })
                    setEmailVerificationId(res.verification_id || null)
                    setEmailCode('')
                    refresh()
                  } catch (e: any) {
                    setEmailError(e?.response?.data?.detail || e?.message || 'Could not start email verification.')
                  } finally {
                    setEmailBusy(false)
                  }
                }}
              >
                Send code
              </SettingsButton>
              <SettingsButton
                variant="primary"
                disabled={emailBusy || !emailCode.trim() || !emailVerificationId}
                onClick={async () => {
                  setEmailBusy(true)
                  setEmailError(null)
                  try {
                    await verifyEmailChange({
                      verification_id: emailVerificationId,
                      code: emailCode.trim(),
                    })
                    refresh()
                    closeEmailModal()
                  } catch (e: any) {
                    setEmailError(e?.response?.data?.detail || e?.message || 'Could not verify email code.')
                  } finally {
                    setEmailBusy(false)
                  }
                }}
              >
                Verify
              </SettingsButton>
            </div>
          </div>
        </div>
      ) : null}

      {phoneOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d1118] p-4">
            <div className="text-lg font-semibold text-white">Manage phone</div>
            <p className="mt-2 text-sm text-white/70">
              Enter a phone number in E.164 format (for example, +15551234567).
            </p>
            <div className="mt-3">
              <div className="mb-1 text-sm text-white/70">Phone</div>
              <input
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="+15551234567"
                className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 outline-none"
              />
            </div>
            <div className="mt-3">
              <div className="mb-1 text-sm text-white/70">Verification code</div>
              <input
                value={phoneCode}
                onChange={(e) => setPhoneCode(e.target.value)}
                placeholder="6-digit code"
                className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 outline-none"
              />
            </div>
            {phoneError ? <div className="mt-3 text-sm text-amber-200">{phoneError}</div> : null}
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <SettingsButton variant="ghost" onClick={closePhoneModal}>
                Close
              </SettingsButton>
              <SettingsButton
                variant="primary"
                disabled={phoneBusy || !phoneInput.trim()}
                onClick={async () => {
                  setPhoneBusy(true)
                  setPhoneError(null)
                  try {
                    await requestPhoneOtp({ phone: phoneInput.trim() })
                    setPhoneOtpRequested(true)
                    refresh()
                  } catch (e: any) {
                    setPhoneError(e?.response?.data?.detail || e?.message || 'Could not send OTP.')
                  } finally {
                    setPhoneBusy(false)
                  }
                }}
              >
                Send code
              </SettingsButton>
              <SettingsButton
                variant="primary"
                disabled={phoneBusy || !phoneOtpRequested || !phoneCode.trim()}
                onClick={async () => {
                  setPhoneBusy(true)
                  setPhoneError(null)
                  try {
                    await verifyPhoneOtp({ code: phoneCode.trim() })
                    refresh()
                    closePhoneModal()
                  } catch (e: any) {
                    setPhoneError(e?.response?.data?.detail || e?.message || 'Could not verify code.')
                  } finally {
                    setPhoneBusy(false)
                  }
                }}
              >
                Verify
              </SettingsButton>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function LinkRow({
  to,
  label,
  value,
  compact = false,
}: {
  to: string
  label: string
  value: string
  compact?: boolean
}) {
  return (
    <Link
      to={to}
      className={[
        'flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 text-white hover:bg-white/10',
        compact ? 'min-h-11 py-2' : 'min-h-11 py-2.5',
      ].join(' ')}
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold">{label}</div>
        <div className="truncate text-xs text-white/65">{value}</div>
      </div>
      <span className="text-white/60">{'>'}</span>
    </Link>
  )
}

function LinkButton({ to, children }: { to: string; children: string }) {
  return (
    <Link
      to={to}
      className="inline-flex min-h-11 items-center justify-center rounded-lg border border-white/15 bg-white/[0.03] px-3 text-sm font-semibold text-white hover:bg-white/10"
    >
      {children}
    </Link>
  )
}

function ActionRow({
  label,
  value,
  actionLabel,
  onClick,
  badge,
  badgeTone,
}: {
  label: string
  value: string
  actionLabel: string
  onClick: () => void
  badge?: string
  badgeTone?: 'ok' | 'warn' | 'muted'
}) {
  const badgeClass =
    badgeTone === 'ok'
      ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100'
      : badgeTone === 'warn'
        ? 'border-amber-400/30 bg-amber-500/10 text-amber-100'
        : 'border-white/20 bg-white/10 text-white/70'

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-left hover:bg-white/10"
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold text-white">{label}</div>
        <div className="truncate text-xs text-white/65">{value}</div>
      </div>
      <div className="flex items-center gap-2">
        {badge ? <span className={`rounded-full border px-2 py-0.5 text-[11px] ${badgeClass}`}>{badge}</span> : null}
        <span className="text-xs text-white/80">{actionLabel}</span>
        <span className="text-white/60">{'>'}</span>
      </div>
    </button>
  )
}

function StaticRow({
  label,
  value,
  badge,
  badgeTone,
}: {
  label: string
  value: string
  badge?: string
  badgeTone?: 'ok' | 'warn' | 'muted'
}) {
  const badgeClass =
    badgeTone === 'ok'
      ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100'
      : badgeTone === 'warn'
        ? 'border-amber-400/30 bg-amber-500/10 text-amber-100'
        : 'border-white/20 bg-white/10 text-white/70'

  return (
    <div className="flex min-h-11 items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-white">{label}</div>
        <div className="truncate text-xs text-white/65">{value}</div>
      </div>
      {badge ? <span className={`rounded-full border px-2 py-0.5 text-[11px] ${badgeClass}`}>{badge}</span> : null}
    </div>
  )
}

function Avatar({ url, alt }: { url?: string; alt: string }) {
  if (url) return <img src={url} alt={alt} className="h-14 w-14 rounded-full object-cover" />
  const initials = (alt || 'PL')
    .split(' ')
    .map((part) => part.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return (
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
      <span className="font-semibold text-white">{initials || 'PL'}</span>
    </div>
  )
}

function formatDate(value?: string | null) {
  if (!value) return 'Recently'
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return value
  }
}

function formatAccountStatus(value: string) {
  const key = value.toLowerCase()
  if (key === 'under_review') return 'Under review'
  if (key === 'limited') return 'Limited'
  if (key === 'disabled') return 'Disabled'
  return 'Active'
}

function accountStatusFromProfile(raw?: string) {
  const key = String(raw || '').toLowerCase()
  if (key.includes('review') || key.includes('hidden_pending')) return 'under_review'
  if (key.includes('limit') || key.includes('hidden')) return 'limited'
  return 'active'
}

function formatPreferredContact(value: string) {
  if (value === 'phone') return 'Phone'
  if (value === 'email') return 'Email'
  return 'None'
}

function getOptionalString(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function formatAgeGroup(value: 'unknown' | 'child' | 'teen' | 'adult'): string {
  if (value === 'adult') return '18+'
  if (value === 'teen') return '13-17'
  if (value === 'child') return 'Under 13'
  return 'Unknown'
}

function formatAgeVerificationStatus(value: 'not_set' | 'self_attested' | 'verified') {
  if (value === 'verified') return 'Verified'
  if (value === 'self_attested') return 'Self-attested'
  return 'Not verified'
}

function formatSensitivePref(value: 'hide' | 'blur' | 'show'): string {
  if (value === 'show') return 'Shown'
  if (value === 'blur') return 'Blurred'
  return 'Hidden'
}

function formatAdultAccessState(value: 'locked' | 'eligible' | 'enabled'): string {
  if (value === 'enabled') return 'Enabled'
  if (value === 'eligible') return 'Eligible'
  return 'Locked'
}
